import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { toolTransform } from '@backrs_ui/common-ui';

import { HoverButton } from '@backrs_ui/common-ui/src/molecules/Nugget/HoverButton';
import { BackrsBannerLogo, BannerTypeChip } from '@backrs_ui/grow-ui/src/components/BannerOverlays';
import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import { getBookmarkIdForNugget } from '@backrs_ui/grow-ui/src/pages/Bookmarks/pages/bookmarks.util';
import {
  useGetBookmarksQuery,
  usePostBookmarkMutation,
  useDeleteBookmarkMutation,
} from '@backrs_ui/grow-ui/src/redux/bookmarks/bookmarks.api';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  openPlaylistListStep,
  setNuggetToAdd,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';
import { useGetToolByToolIdQuery } from '@backrs_ui/grow-ui/src/redux/tools/tools.api';
import { GROW_URL } from '@backrs_ui/grow-ui/src/settings';

import { Box, SxProps, Theme } from '@mui/material';

export type GuidePageBanner = Readonly<{
  src: string;
  /** Optional mobile-specific banner image. Falls back to `src` when omitted. */
  mobileSrc?: string;
  alt: string;
}>;

type GuideBannerProps = Readonly<{
  /** Tool backing the guide, which the bookmark and add-task actions act on. */
  toolId: string;
  banner: GuidePageBanner;
}>;

/** Guide hero image, with bookmark, share and add-task actions revealed on hover. */
export const GuideBanner: React.FC<GuideBannerProps> = ({ toolId, banner }) => {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const { loggedIn } = useAuth();

  const { data: bookmarks } = useGetBookmarksQuery(undefined, { skip: !loggedIn });
  const [postBookmark] = usePostBookmarkMutation();
  const [deleteBookmark] = useDeleteBookmarkMutation();
  const { data: guideTool } = useGetToolByToolIdQuery(toolId);

  const [isHovered, setIsHovered] = useState(false);

  const bookmarkId = guideTool
    ? getBookmarkIdForNugget(bookmarks, guideTool.id, 'tool')
    : undefined;

  const handleBookmark = useCallback(async () => {
    if (!guideTool) return;

    if (bookmarkId) {
      await deleteBookmark(bookmarkId);
    } else {
      await postBookmark({ type: 'tool', itemId: guideTool.id });
    }
  }, [bookmarkId, deleteBookmark, postBookmark, guideTool]);

  const handleShare = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(`${GROW_URL}${pathname}`);
    } catch (error) {
      console.error('Failed to copy link to clipboard:', error);
    }
  }, [pathname]);

  const handleAddTask = useCallback(() => {
    if (!guideTool) return;

    dispatch(openPlaylistListStep());
    dispatch(setNuggetToAdd(toolTransform(guideTool)));
  }, [dispatch, guideTool]);

  return (
    <Box sx={containerSx}>
      <Box
        sx={imageCardSx}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {banner.mobileSrc ? (
          <>
            <Box component="img" src={banner.src} alt={banner.alt} sx={imageDesktopSx} />
            <Box component="img" src={banner.mobileSrc} alt={banner.alt} sx={imageMobileSx} />
          </>
        ) : (
          <Box component="img" src={banner.src} alt={banner.alt} sx={imageSingleSx} />
        )}
        <BackrsBannerLogo />
        <BannerTypeChip toolType="guide" />
        {guideTool && (
          <HoverButton
            isVisible={isHovered}
            idPrefix="guide-page-hero"
            hasBookmarked={!!bookmarkId}
            handleActionBookmark={handleBookmark}
            handleActionShare={handleShare}
            handleActionAddTask={handleAddTask}
          />
        )}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  containerType: 'inline-size',
  width: '100%',
};

const imageCardSx: SxProps<Theme> = ({ backrs }) => ({
  overflow: 'hidden',
  width: '100%',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  backgroundColor: backrs.colors['Colors/Foreground/fg-primary'],
  position: 'relative',
});

const imageBaseSx: SxProps<Theme> = {
  width: '100%',
  maxHeight: 180,
  height: 'auto',
  objectFit: 'contain',
  objectPosition: 'center',
};

const imageSingleSx: SxProps<Theme> = {
  ...imageBaseSx,
  display: 'block',
};

const imageDesktopSx: SxProps<Theme> = {
  ...imageBaseSx,
  display: 'block',
  '@container (max-width: 599px)': {
    display: 'none',
  },
};

const imageMobileSx: SxProps<Theme> = {
  ...imageBaseSx,
  display: 'none',
  '@container (max-width: 599px)': {
    display: 'block',
  },
};
